/**
 * MetroTicket — vertical boarding-pass style hero ticket.
 *
 * Features
 * --------
 *   • Real QR code (qrcode.react) encoding the public /journey/<tid>
 *     URL. Scans on phones.
 *   • Inky UCAS/Common-App cycle stamp overlaid at a slight angle so
 *     the ticket feels dated.
 *   • Hover-flip (desktop) / tap-flip (mobile) to reveal the back —
 *     a live days-to-deadline countdown, the student's next step and
 *     share controls.
 *   • Vertical ("boarding-pass") orientation by default. Pass
 *     `vertical={false}` to render horizontally (used on mobile hero).
 *   • Resting tilt ±1-2°; hover-flip replaces the swing tilt.
 */
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Compass, Flag, MapPin, GraduationCap, Train, Share2,
  Clock, ArrowRight, RotateCcw,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import {
  cycleLabelForYear, nextDeadlineForYear, daysBetween,
} from "@/lib/cycleDeadlines";

// Natural dimensions of the un-rotated horizontal ticket.
const BASE_W = 540;
const BASE_H = 300;

export default function MetroTicket({ data, publicUrl, vertical = true, maxScale = 1, fixedScale = null }) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(fixedScale ?? 1);
  const [flipped, setFlipped] = useState(false);

  // One random resting tilt per mount. We DON'T swing on hover any
  // more — the hover reveals the back of the ticket instead.
  const restTilt = useMemo(() => {
    const sign = Math.random() < 0.5 ? -1 : 1;
    const rest = sign * (1 + Math.random() * 1.5);
    const bias = vertical ? 90 : 0;
    return `rotate(${(bias + rest).toFixed(2)}deg)`;
  }, [vertical]);

  useLayoutEffect(() => {
    // When `fixedScale` is set, the caller wants a non-responsive
    // ticket — bypass the ResizeObserver entirely so the top strip
    // (and the whole card) stays exactly this size on every
    // viewport.
    if (fixedScale != null) {
      setScale(fixedScale);
      return;
    }
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      const target = vertical ? BASE_H : BASE_W;
      setScale(Math.min(maxScale, w / target));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [vertical, maxScale, fixedScale]);

  if (!data) return null;

  const dest = data.destination || "To be revealed";
  const origin = data.origin || "—";
  const name = (data.name && data.name !== "Future Student")
    ? data.name
    : "Add your name";
  const subject = data.carriage || "Pick a subject";
  const stoppingAt = data.stopping_at || "Shortlist a uni";
  const tid = (data.ticket_id || "").toUpperCase();
  const match = Math.max(0, Math.min(100, data.match_score || 0));
  const year = data.year_of_entry;
  const validLabel = year ? `SEP ${year}` : "TBC";
  const cycleStamp = cycleLabelForYear(year);         // "UCAS 2027" | null
  const deadlineInfo = nextDeadlineForYear(year);     // {name, date} | null
  const daysLeft = deadlineInfo
    ? daysBetween(new Date(), deadlineInfo.date)
    : null;

  const footprintHeight = (vertical ? BASE_W : BASE_H) * scale;

  // Build the fully qualified URL for the QR code so a scan opens
  // the public journey page in any browser, not just this origin.
  const qrUrl = publicUrl
    ? `${typeof window !== "undefined" ? window.location.origin : ""}${publicUrl}`
    : "";

  return (
    <div
      ref={wrapRef}
      className="relative w-full"
      style={{
        height: footprintHeight,
        // Asymmetric paper shadow — stays in world coordinates
        // ("below" the ticket) regardless of the ticket's tilt. No
        // top-side glow.
        filter:
          "drop-shadow(0 23px 14px rgba(76,29,149,0.18)) drop-shadow(0 14px 6px rgba(76,29,149,0.12))",
      }}
      data-testid="metro-ticket"
    >
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          transition: "transform 500ms cubic-bezier(0.22,1,0.36,1)",
          transform: restTilt,
        }}
      >
        {/* 2D cross-fade flip container — no 3D, no preserve-3d.
            Flipping between faces is a plain opacity swap so the
            whole stack sits in the normal 2D rendering context and
            can host a protruding stub without clipping quirks. */}
        <div
          onMouseEnter={() => setFlipped(true)}
          onMouseLeave={() => setFlipped(false)}
          onClick={() => setFlipped((v) => !v)}
          style={{
            width: BASE_W,
            height: BASE_H,
            position: "relative",
            transform: `scale(${scale})`,
            transformOrigin: "center center",
            cursor: "pointer",
          }}
          data-testid="metro-ticket-flip"
          data-flipped={flipped ? "true" : "false"}
        >
          {/* Front face */}
          <div
            className="absolute inset-0"
            style={{
              opacity: flipped ? 0 : 1,
              transition: "opacity 220ms ease-out",
              pointerEvents: flipped ? "none" : "auto",
            }}
          >
            <TicketFront
              name={name}
              origin={origin}
              destination={dest}
              destinationFull={data.destination_full}
              subject={subject}
              stoppingAt={stoppingAt}
              match={match}
              tid={tid}
              publicUrl={publicUrl}
              qrUrl={qrUrl}
              validLabel={validLabel}
              cycleStamp={cycleStamp}
              daysLeft={daysLeft}
            />
          </div>

          {/* Back face */}
          <div
            className="absolute inset-0"
            style={{
              opacity: flipped ? 1 : 0,
              transition: "opacity 220ms ease-out",
              pointerEvents: flipped ? "auto" : "none",
            }}
          >
            <TicketBack
              name={name}
              deadlineInfo={deadlineInfo}
              daysLeft={daysLeft}
              tid={tid}
              publicUrl={publicUrl}
              qrUrl={qrUrl}
              match={match}
              nextStep={deriveNextStep(data)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Front face ────────────────────────────────────────────────

function TicketFront({
  name, origin, destination, destinationFull,
  subject, stoppingAt, match, tid, publicUrl, qrUrl,
  validLabel, cycleStamp, daysLeft,
}) {
  return (
    <div
      className="relative w-full h-full bg-white rounded-[14px]"
      style={{
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)",
      }}
    >
      {/* Top band */}
      <div className="relative h-[64px] bg-gradient-to-r from-[#1E1B4B] via-[#3B0764] to-[#4C1D95] rounded-t-[14px] flex items-center px-4 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg, rgba(255,255,255,0.5) 0 2px, transparent 2px 7px)",
          }}
        />
        <img
          src="/logo.png"
          alt="Which University Advisor"
          className="relative h-9 w-auto drop-shadow-sm"
          data-testid="metro-ticket-logo"
        />
        <div className="relative ml-auto text-right text-white">
          <div className="text-[9px] tracking-[0.22em] font-semibold uppercase opacity-80">
            Journey · 1 Trip
          </div>
          <div
            className="text-[11px] font-mono tracking-tight opacity-90 mt-0.5"
            data-testid="metro-valid-label"
          >
            VALID · {validLabel}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="relative flex h-[calc(100%-64px)]">
        {/* Inky cycle stamp — rendered as a 30% opacity watermark
            that sits behind the text, so it reads like an ink
            impression that was stamped onto the paper before the
            ticket details were printed. pointer-events-none so it
            never steals hover from the ticket. */}
        {cycleStamp && (
          <div
            aria-hidden
            data-testid="metro-cycle-stamp"
            className="absolute pointer-events-none"
            style={{
              left: "59px",
              bottom: "21px",
              transform: "rotate(-10deg)",
              opacity: 0.15,
              zIndex: 0,
            }}
          >
            <div
              className="border-[2px] border-[#9F1239] rounded-md px-2 py-[1px] text-[#9F1239] font-black tracking-[0.22em] text-[11px] uppercase"
              style={{
                boxShadow: "inset 0 0 0 1px rgba(159,18,57,0.6)",
                mixBlendMode: "multiply",
                filter: "contrast(0.95)",
              }}
            >
              {cycleStamp}
            </div>
          </div>
        )}

        {/* Left — journey info */}
        <div className="flex-1 px-5 pt-4 pb-4 flex flex-col justify-between relative z-10">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[9px] tracking-[0.22em] text-stone-500 font-bold">
                STUDENT CARD
              </div>
              <div className="text-[16px] font-black text-stone-900 leading-tight truncate">
                {name}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[9px] tracking-[0.22em] text-stone-500 font-bold">
                TICKET №
              </div>
              <div className="text-[11px] font-mono font-bold text-stone-800 tracking-tight">
                {tid || "— — — —"}
              </div>
              {typeof daysLeft === "number" && daysLeft >= 0 && (
                <div
                  className="mt-0.5 inline-flex items-center gap-1 text-[9px] font-semibold text-[#9F1239] bg-[#FFE4E6]/70 rounded-sm px-1.5 py-0.5"
                  data-testid="metro-deadline-countdown"
                  title="Days to next cycle deadline"
                >
                  <Clock className="h-2.5 w-2.5" />
                  {daysLeft}d to go
                </div>
              )}
            </div>
          </div>

          <div className="flex items-start gap-2">
            <div className="flex items-start gap-2 shrink-0 w-[110px]">
              <div className="mt-3 h-7 w-7 rounded-full bg-[#F5F3FF] text-[#5B21B6] flex items-center justify-center shrink-0">
                <MapPin className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0">
                <div className="text-[9px] tracking-[0.22em] text-stone-500 font-bold">
                  ORIGIN
                </div>
                <div
                  className="text-[13px] font-bold text-stone-900 leading-tight break-words"
                  data-testid="metro-origin"
                >
                  {origin}
                </div>
              </div>
            </div>

            <div className="relative h-7 flex items-center shrink-0 w-8 mt-3">
              <div
                className="w-full"
                style={{
                  height: 2,
                  backgroundImage:
                    "radial-gradient(circle, #CBD5E1 1px, transparent 1.5px)",
                  backgroundSize: "6px 2px",
                  backgroundRepeat: "repeat-x",
                }}
              />
              <Train className="absolute left-1/2 -translate-x-1/2 h-4 w-4 text-[#7C3AED]" />
            </div>

            <div className="flex items-start gap-2 min-w-0 flex-1">
              <div className="mt-3 h-7 w-7 rounded-full bg-[#FEF3C7] text-[#92400E] flex items-center justify-center shrink-0">
                <Flag className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[9px] tracking-[0.22em] text-[#5B21B6] font-bold leading-tight">
                  FINAL<br />DESTINATION
                </div>
                <div
                  className={`${(destination || "").length > 36 ? "text-[13px] sm:text-[15px]" : "text-[18px]"} font-black text-[#4C1D95] leading-tight break-words mt-0.5 line-clamp-5 sm:line-clamp-3`}
                  title={destinationFull && destinationFull !== destination ? destinationFull : undefined}
                  data-testid="metro-destination"
                >
                  {destination}
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-start gap-x-5 gap-y-2 text-[11px] text-stone-700">
            <div className="flex items-start gap-2 min-w-0 flex-1">
              <div className="h-6 w-6 rounded-full bg-[#F0FDF4] text-emerald-700 flex items-center justify-center shrink-0 mt-0.5">
                <Compass className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[8px] tracking-[0.22em] text-stone-500 font-bold">
                  CARRIAGE
                </div>
                <div className="text-[12px] font-bold text-stone-900 leading-tight break-words">
                  {subject}
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2 min-w-0 flex-1">
              <div className="h-6 w-6 rounded-full bg-[#EFF6FF] text-blue-700 flex items-center justify-center shrink-0 mt-0.5">
                <GraduationCap className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[8px] tracking-[0.22em] text-stone-500 font-bold">
                  VIA
                </div>
                <div
                  className="text-[12px] font-bold text-stone-900 leading-tight break-words"
                  data-testid="metro-via"
                >
                  {stoppingAt}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Perforation */}
        <div className="relative w-[1px]">
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(180deg, rgba(0,0,0,0.25) 50%, transparent 50%)",
              backgroundSize: "1px 8px",
            }}
          />
          <div className="absolute -left-[7px] -top-[7px] h-3.5 w-3.5 rounded-full bg-white border-l border-stone-100" />
          <div className="absolute -left-[7px] -bottom-[7px] h-3.5 w-3.5 rounded-full bg-white border-l border-stone-100" />
        </div>

        {/* Right stub — match ring + real QR */}
        <div className="w-[132px] bg-[#FAFAF8] rounded-br-[14px] px-4 py-3 flex flex-col items-center justify-between">
          <MatchRing percent={match} />
          <div className="flex flex-col items-center gap-1">
            {qrUrl ? (
              <QRCodeSVG
                value={qrUrl}
                size={72}
                level="M"
                marginSize={1}
                fgColor="#111827"
                data-testid="metro-ticket-qr"
                aria-label="Scan to open your journey page"
              />
            ) : (
              <div className="h-[72px] w-[72px] bg-white border border-stone-300 rounded-sm" />
            )}
            <div className="text-[8px] tracking-[0.22em] text-stone-500 font-bold uppercase">
              Scan me
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Back face ────────────────────────────────────────────────

function TicketBack({ name, deadlineInfo, daysLeft, tid, publicUrl, qrUrl, match, nextStep }) {
  const [copied, setCopied] = useState(false);
  const copy = async (e) => {
    e.stopPropagation();
    if (!qrUrl) return;
    try {
      await navigator.clipboard.writeText(qrUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      window.prompt("Copy this link:", qrUrl);
    }
  };

  return (
    <div
      className="relative w-full h-full rounded-[14px] overflow-hidden bg-white"
      style={{
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)",
      }}
      data-testid="metro-ticket-back"
    >
      {/* Subtle ticket "stripes" pattern across the whole back */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(90deg, rgba(76,29,149,1) 0 2px, transparent 2px 10px)",
        }}
      />

      {/* Top band — mirrors the front's 64px dark band so the back
          reads as the same boarding pass silhouette. */}
      <div className="relative h-[64px] bg-gradient-to-r from-[#1E1B4B] via-[#3B0764] to-[#4C1D95] rounded-t-[14px] flex items-center px-4 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg, rgba(255,255,255,0.5) 0 2px, transparent 2px 7px)",
          }}
        />
        <div className="relative text-white">
          <div className="text-[9px] tracking-[0.22em] font-semibold uppercase opacity-80">
            Reverse side
          </div>
          <div className="text-[14px] font-black truncate max-w-[180px]">
            {name}
          </div>
        </div>
        <div className="relative ml-auto text-right text-white">
          <div className="text-[9px] tracking-[0.22em] font-semibold uppercase opacity-80">
            Ticket №
          </div>
          <div className="text-[11px] font-mono tracking-tight opacity-90 mt-0.5">
            {tid || "—"}
          </div>
        </div>
      </div>

      {/* Body — mirrors the front: left panel + perforation + stub. */}
      <div className="relative flex h-[calc(100%-64px)]">
        {/* Left — reverse-side content (countdown + next step + share) */}
        <div className="relative flex-1 min-w-0 px-5 py-4 flex flex-col text-stone-900">
          {deadlineInfo ? (
            <div>
              <div className="text-[9px] tracking-[0.22em] uppercase text-stone-500">
                Next deadline
              </div>
              <div className="flex items-baseline gap-2 mt-0.5">
                <div
                  className="text-[32px] font-black leading-none text-[#4C1D95]"
                  data-testid="metro-back-days"
                >
                  {Math.max(0, daysLeft)}
                </div>
                <div className="text-[10px] font-semibold text-stone-700 leading-tight">
                  days to {deadlineInfo.name}
                </div>
              </div>
              <div className="text-[10px] text-stone-500 mt-1">
                {deadlineInfo.date.toLocaleDateString(undefined, {
                  weekday: "short", day: "numeric", month: "short", year: "numeric",
                })}
              </div>
            </div>
          ) : (
            <div className="text-[11px] text-stone-600 leading-snug">
              Add your year of entry on your profile to see the countdown to your UCAS or Common App deadline.
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-stone-200">
            <div className="text-[9px] tracking-[0.22em] uppercase text-stone-500">
              Next step
            </div>
            <div className="text-[11px] font-semibold mt-1 leading-snug text-stone-800">
              {nextStep}
            </div>
          </div>

          <div className="mt-auto pt-3 flex items-center gap-2">
            {publicUrl && (
              <>
                <button
                  type="button"
                  onClick={copy}
                  data-testid="metro-ticket-share"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#4C1D95] text-white text-[11px] font-semibold px-2.5 py-1.5 rounded-md hover:bg-[#3B0764] transition-colors"
                >
                  <Share2 className="h-3 w-3" />
                  {copied ? "Copied" : "Copy link"}
                </button>
                <Link
                  to={publicUrl}
                  onClick={(e) => e.stopPropagation()}
                  data-testid="metro-ticket-open"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 bg-white border border-[#4C1D95]/30 text-[#4C1D95] text-[11px] font-semibold px-2.5 py-1.5 rounded-md hover:bg-[#F5F3FF] transition-colors"
                >
                  Open
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Perforation */}
        <div className="relative w-[1px]">
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(180deg, rgba(0,0,0,0.25) 50%, transparent 50%)",
              backgroundSize: "1px 8px",
            }}
          />
          <div className="absolute -left-[7px] -top-[7px] h-3.5 w-3.5 rounded-full bg-[#F5F0E8]" />
          <div className="absolute -left-[7px] -bottom-[7px] h-3.5 w-3.5 rounded-full bg-[#F5F0E8]" />
        </div>

        {/* Right stub — identical to the front: match ring + QR, on a
            cream panel so the ticket silhouette matches the front
            exactly when the user flips. */}
        <div className="w-[132px] bg-[#FAFAF8] rounded-br-[14px] px-4 py-3 flex flex-col items-center justify-between">
          <MatchRing percent={match} />
          <div className="flex flex-col items-center gap-1">
            {qrUrl ? (
              <QRCodeSVG
                value={qrUrl}
                size={72}
                level="M"
                marginSize={1}
                fgColor="#111827"
                aria-label="Scan to open your journey page"
              />
            ) : (
              <div className="h-[72px] w-[72px] bg-white border border-stone-300 rounded-sm" />
            )}
            <div className="text-[8px] tracking-[0.22em] text-stone-500 font-bold uppercase">
              Scan me
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Shared bits ──────────────────────────────────────────────

function MatchRing({ percent }) {
  const r = 28;
  const c = 2 * Math.PI * r;
  const dash = (percent / 100) * c;
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width="70" height="70" viewBox="0 0 70 70">
        <circle cx="35" cy="35" r={r} fill="none" stroke="#E9D5FF" strokeWidth="5" />
        <circle
          cx="35" cy="35" r={r} fill="none"
          stroke="#7C3AED" strokeWidth="5" strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          transform="rotate(-90 35 35)"
          style={{ transition: "stroke-dasharray 400ms ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-[14px] font-black text-[#4C1D95] leading-none font-mono">
          {percent}%
        </div>
        <div className="text-[8px] tracking-[0.2em] font-bold text-[#5B21B6] uppercase mt-0.5">
          Match
        </div>
      </div>
    </div>
  );
}


function deriveNextStep(data) {
  if (!data.name || data.name === "Future Student") return "Add your name on your profile";
  if (!data.origin || data.origin === "—")           return "Tell us where you live";
  if (!data.carriage)                                return "Pick an intended subject";
  if (!data.destination)                             return "Add your dream career";
  if (!data.stopping_at)                             return "Shortlist your first university";
  return "Share your ticket with family and friends";
}
